import JSZip from "jszip"
import appInsightsLogger from "../../features/logging/appInsightsLogger"
import { DataStatus } from "../enums"
import { FileStatusDisplayIdEnum } from "./schema"
import axios from "axios"
import { downloadFileBlob } from "../../features/shared/utils"

export const getFileStatusDisplayId = (
  status: number,
): FileStatusDisplayIdEnum => {
  switch (status) {
    case 200:
      return FileStatusDisplayIdEnum.AVAILABLE
    case 202:
      return FileStatusDisplayIdEnum.PROCESSING
    case 410:
      return FileStatusDisplayIdEnum.QUARANTINED
    default:
      return FileStatusDisplayIdEnum.ERROR
  }
}

interface DownloadZipFileOptions {
  fileDownloadInfo: Array<{
    downloadUrl: string
    fileName: string
  }>
  downloadFileNameNoExt?: string
  setDataStatus?: (status: DataStatus) => void
}
/**
 *  Downloads multiple files as a zip. If any file is still processing (202), it will be skipped. If all files fail, an error will be thrown.
 *
 *  TODO: We could create and add this to a general fileApi service file since it does not belong to any specific domain.
 *
 *  @param options.fileDownloadInfo - An array of objects containing the download URL and file name for each file to be downloaded.
 *  @param options.downloadFileNameNoExt - The base name for the zip file (without extension).
 *  @param options.setDataStatus - Optional callback to update the data status.
 *  @returns A promise that resolves to true if the zip file was successfully created and downloaded, or false if there was an error.
 */
export const downloadZip = async (options: DownloadZipFileOptions) => {
  const { downloadFileNameNoExt, fileDownloadInfo, setDataStatus } = options

  // Filter out any file info that is missing required fields
  const safeFileDownloadInfo = fileDownloadInfo.filter(
    (fileInfo) => !!fileInfo.downloadUrl && !!fileInfo.fileName,
  )
  if (!safeFileDownloadInfo.length) {
    throw new Error("File download information required")
  }
  if (safeFileDownloadInfo.length !== fileDownloadInfo.length) {
    appInsightsLogger.traceWarning(
      "Some file download information is missing required fields and will be skipped",
    )
  }

  try {
    setDataStatus?.(DataStatus.SENDING)

    const zip = new JSZip()
    const downloadPromises: Promise<void>[] = []

    // Create promises to download each file
    safeFileDownloadInfo.forEach((fileInfo) => {
      const url = fileInfo.downloadUrl
      const downloadPromise = axios
        .get(url, { responseType: "blob" })
        .then((response) => {
          if (response.status === 200) {
            const fileBlob: Blob = response.data

            // Check if we have a dupe filename
            // If so, append the current  UTC millisecond timestamp of download datetime as a suffix to make it unique
            const isDupeFileName =
              fileDownloadInfo.filter(
                (info) => info.fileName === fileInfo.fileName,
              ).length > 1
            let finalFileName = fileInfo.fileName
            if (isDupeFileName) {
              const timestamp = Date.now().toString()
              const fileNameParts = fileInfo.fileName.split(".")
              fileNameParts.splice(fileNameParts.length - 1, 0, timestamp)
              finalFileName = fileNameParts.join(".")
            }
            zip.file(finalFileName, fileBlob)
          } else if (response.status === 202) {
            // File is still processing, skip it
            appInsightsLogger.traceWarning(
              `File ${fileInfo.fileName} is still processing, skipping`,
            )
          }
        })
        .catch((error) => {
          // Continue with other files even if one fails
          appInsightsLogger.traceError(
            `Failed to download file ${fileInfo.fileName}:`,
            error,
          )
        })

      downloadPromises.push(downloadPromise)
    })

    // Wait for all downloads to complete
    await Promise.all(downloadPromises)

    // Check if we have any files in the zip
    if (Object.keys(zip.files).length === 0) {
      throw new Error("No files were downloaded")
    }

    // Generate the zip file with license-specific filename
    const zipBlob = await zip.generateAsync({ type: "blob" })

    // Download the zip file
    const zipFilename = `${downloadFileNameNoExt}.zip`
    downloadFileBlob(zipBlob, zipFilename)

    setDataStatus?.(DataStatus.LOADED)
    return true
  } catch (error) {
    appInsightsLogger.traceError("Error creating zip file:", error)
    setDataStatus?.(DataStatus.ERROR)
    return false
  }
}
